import {
  HttpContextToken,
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { catchError, throwError } from 'rxjs';
import { inject, Injector } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TranslatedToastService } from '../_services/translated-toast.service';
import { ErrorMessageService } from '../_services/error-message.service';

// Set by a component that shows its own error toast; it gets the original HttpErrorResponse
export const SkipErrorToast = new HttpContextToken<boolean>(() => false);

export const errorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const injector = inject(Injector);
  const toastr = inject(ToastrService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // DO NOT HANDLE ERRORS NEEDED FOR FORM VALIDATION
      if (
        req.url.includes('https://graph.microsoft.com/v1.0/directoryObjects') ||
        /https:\/\/bernerfachhochschule\.sharepoint\.com\/sites\/.*\/_api\/web\/url/.test(
          req.url,
        ) ||
        (req.url.includes('https://graph.microsoft.com/v1.0/users') &&
          error.status === 404)
      )
        return throwError(() => error);

      // CHECK FOR SP-SITECREATION-ERROR WHEN NAME IS USED IN RECYCLE-BIN
      if (
        error.status === 500 &&
        req.url.includes('/api/sites') &&
        error.error &&
        ((error.error.message &&
          (error.error.message.includes('siteStatus = 3') ||
            error.error.message.includes('sitesStatus = 3'))) ||
          (error.error.details &&
            (error.error.details.includes('siteStatus = 3') ||
              error.error.details.includes('sitesStatus = 3'))))
      ) {
        console.error('Site name conflict detected (siteStatus = 3):', error);

        try {
          const translatedToastr = injector.get(TranslatedToastService);

          translatedToastr
            .show(
              'error',
              'TOASTS.SHAREPOINT.NAME_IN_USE_MESSAGE',
              'TOASTS.SHAREPOINT.NAME_IN_USE_TITLE',
            )
            .catch((err) =>
              console.error('Failed to show translated toast:', err),
            );
        } catch (err) {
          console.error('Could not resolve TranslatedToastService:', err);
          toastr.error(
            'This site name is already in use. Please choose a different site name.',
            'Site Name Already in Use',
          );
        }

        const handledError = new Error('Site name already in use') as any;
        handledError.handledByInterceptor = true;
        return throwError(() => handledError);
      }

      if (req.context.get(SkipErrorToast)) return throwError(() => error);

      console.error('HTTP error intercepted:', error);

      // Resolved lazily: TranslateService loads its files through HttpClient, i.e. through this interceptor
      try {
        injector
          .get(ErrorMessageService)
          .showToast(error, 'TOASTS.ERROR.TITLE')
          .catch((err) => console.error('Failed to show translated toast:', err));
      } catch (err) {
        console.error('Could not resolve ErrorMessageService:', err);
        toastr.error(error.message, 'Server Error');
      }

      // Mark this error as handled by the interceptor
      const handledError = new Error(
        `HTTP ${error.status}: ${error.message}`,
      ) as any;
      handledError.handledByInterceptor = true;

      return throwError(() => handledError);
    }),
  );
};
